<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, toRef, watch } from 'vue'
import { useModalScrollLock } from '@/composables/useModalScrollLock'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    ariaLabelledby?: string
    ariaDescribedby?: string
    closeDisabled?: boolean
    closeOnOverlay?: boolean
    hideClose?: boolean
    overlayClass?: string
    modalClass?: string | string[] | Record<string, boolean>
    closeLabel?: string
  }>(),
  {
    closeDisabled: false,
    closeOnOverlay: true,
    hideClose: false,
    closeLabel: 'Close',
  }
)

const emit = defineEmits<{
  close: []
}>()

const dialogRef = ref<HTMLElement | null>(null)
const previouslyFocused = ref<HTMLElement | null>(null)

useModalScrollLock(toRef(props, 'isOpen'))

const requestClose = () => {
  if (props.closeDisabled) {
    return
  }
  emit('close')
}

const onOverlayClick = (event: MouseEvent) => {
  if (!props.closeOnOverlay || props.closeDisabled) {
    return
  }
  if (event.target === event.currentTarget) {
    requestClose()
  }
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (!props.isOpen || props.closeDisabled || event.key !== 'Escape') {
    return
  }
  event.preventDefault()
  requestClose()
}

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      previouslyFocused.value = document.activeElement as HTMLElement | null
      await nextTick()
      dialogRef.value?.focus()
      return
    }

    previouslyFocused.value?.focus?.()
    previouslyFocused.value = null
  }
)

onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onDocumentKeydown)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="sa-modal-overlay"
      :class="[overlayClass, { 'sa-modal-overlay--open': isOpen }]"
      @click="onOverlayClick"
    >
      <div
        ref="dialogRef"
        class="sa-modal"
        :class="modalClass"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="ariaLabelledby"
        :aria-describedby="ariaDescribedby"
        tabindex="-1"
        @click.stop
      >
        <header v-if="!hideClose || $slots['header-start']" class="sa-modal__header">
          <div v-if="$slots['header-start']" class="sa-modal__header-start">
            <slot name="header-start" />
          </div>
          <button
            v-if="!hideClose"
            type="button"
            class="sa-modal-close"
            :aria-label="closeLabel"
            :disabled="closeDisabled"
            @click="requestClose"
          >
            ✕
          </button>
        </header>

        <div class="sa-modal__body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="sa-modal__footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>
